import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector, useAppDispatch } from 'app/store';
import {
  fetchUpdateReleaseValidationsEffect,
  updateReleaseEffect,
} from 'app/store/Releases/effects';
import { getUpdateReleaseValidations } from 'app/store/Releases/selectors';
import { UpdateReleaseValidations } from 'types/releases';
import { getReleaseTitleFormValidations } from 'app/components/Releases/ReleaseTitleForm/utils';
import { getDirtyValues } from 'app/utils/utils';
import { ChakraFormBaseInput } from 'app/components/Core';

interface ReleaseTitleFormComponentProps {
  title: string;
  updateTitle: (title: string) => Promise<void>;
  validations?: UpdateReleaseValidations;
}

interface ReleaseTitleFormData {
  title: string;
}

export const ReleaseTitleFormComponent: React.FC<ReleaseTitleFormComponentProps> = ({
  updateTitle,
  title,
  validations,
}) => {
  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { dirtyFields, errors, isValid },
  } = useForm<ReleaseTitleFormData>({
    defaultValues: { title },
    mode: 'all',
    resolver: yupResolver(getReleaseTitleFormValidations(validations)),
  });
  const [lastValidTitle, setLastValidTitle] = useState(title);

  const onSubmit = handleSubmit((data: ReleaseTitleFormData) => {
    reset(data);
    const dirtyValues = getDirtyValues<ReleaseTitleFormData>(dirtyFields, data);
    if (dirtyValues.title) {
      updateTitle(data.title);
    }
  });

  return (
    <ChakraFormBaseInput
      placeholder="Title"
      {...register('title')}
      isInvalid={Boolean(errors?.title)}
      errorMessage={errors?.title?.message}
      data-testid="title-input"
      onChange={(e) => {
        const value = e.target.value.trimStart();
        setValue('title', value, {
          shouldDirty: true,
          shouldValidate: true,
        });
        if (value) {
          setLastValidTitle(value);
        }
      }}
      onBlur={(e) => {
        if (!e.target.value) {
          setValue('title', lastValidTitle, {
            shouldValidate: true,
            shouldDirty: false,
            shouldTouch: true,
          });
        }
        if (isValid) {
          onSubmit();
        }
      }}
      fontSize="2xl"
      fontWeight={600}
      lineHeight="normal"
    />
  );
};

interface ReleaseTitleFormProps {
  title: string;
}

export const ReleaseTitleForm: React.FC<ReleaseTitleFormProps> = ({
  title,
}) => {
  const dispatch = useAppDispatch();
  const updateReleaseValidations = useAppSelector(getUpdateReleaseValidations);

  const updateTitle = async (title: string) => {
    dispatch(updateReleaseEffect({ title }));
  };

  useEffect(() => {
    if (!updateReleaseValidations) {
      dispatch(fetchUpdateReleaseValidationsEffect());
    }
  }, [dispatch, updateReleaseValidations]);

  return (
    <ReleaseTitleFormComponent
      title={title}
      updateTitle={updateTitle}
      validations={updateReleaseValidations}
    />
  );
};
